import type { ISocialLinks, IBrandSocialProfile } from '../redux/buzzbridge/types';

export type BrandManualSocialPlatform = keyof Pick<ISocialLinks, 'instagram' | 'tiktok' | 'youtube'>;

export interface IBrandManualSocialLinkData {
    /** Derived on save — not entered in the form. */
    url: string;
    username: string;
    follower_count: string;
    engagement_rate: string;
}

export const EMPTY_BRAND_MANUAL_SOCIAL_LINK: IBrandManualSocialLinkData = {
    url: '',
    username: '',
    follower_count: '',
    engagement_rate: '',
};

const parseCount = (value: string): number | undefined => {
    const n = Number(
        String(value ?? '')
            .replace(/,/g, '')
            .trim(),
    );
    return Number.isFinite(n) && n >= 0 ? n : undefined;
};

const parseRate = (value: string): number | undefined => {
    const raw = String(value ?? '')
        .replace(/%/g, '')
        .trim();
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
};

export function normalizeBrandManualHandle(raw: string): string {
    return raw.trim().replace(/^@+/, '');
}

/** Build canonical profile URL from platform + handle. */
export function brandManualSocialUrlFromHandle(platform: BrandManualSocialPlatform, username: string): string {
    const handle = normalizeBrandManualHandle(username);
    if (!handle) return '';

    switch (platform) {
        case 'instagram':
            return `https://www.instagram.com/${handle}`;
        case 'tiktok':
            return `https://www.tiktok.com/@${handle}`;
        case 'youtube':
            return `https://www.youtube.com/@${handle}`;
        default:
            return '';
    }
}

export function isBrandManualSocialLinkComplete(data: IBrandManualSocialLinkData | undefined): boolean {
    return Boolean(normalizeBrandManualHandle(data?.username ?? ''));
}

export function brandManualSocialLinkFromProfile(profile: IBrandSocialProfile): IBrandManualSocialLinkData {
    const platform = String(profile.platform ?? '').toLowerCase() as BrandManualSocialPlatform;
    const username =
        normalizeBrandManualHandle(profile.username ?? profile.display_name ?? '') ||
        normalizeBrandManualHandle(profile.url?.split('/').filter(Boolean).pop() ?? '');

    return {
        url: profile.url ?? brandManualSocialUrlFromHandle(platform, username),
        username,
        follower_count: profile.follower_count != null ? String(profile.follower_count) : '',
        engagement_rate: profile.engagement_rate != null ? String(profile.engagement_rate) : '',
    };
}

export function brandManualSocialLinkToApi(platform: BrandManualSocialPlatform, data: IBrandManualSocialLinkData) {
    const username = normalizeBrandManualHandle(data.username);
    const url = brandManualSocialUrlFromHandle(platform, username);
    const follower_count = parseCount(data.follower_count);
    const engagement_rate = parseRate(data.engagement_rate);

    return {
        url,
        linked: false as const,
        username,
        ...(follower_count != null && { follower_count }),
        ...(engagement_rate != null && { engagement_rate }),
    };
}
